import { createApp } from 'vue'
import App from './App.vue'
import { router } from './router'
import axios from 'axios'
import moment from 'moment'
import { auth0 } from './scripts/auth/auth0'
import { createPopper } from '@popperjs/core'
import VueTransitions from '@morev/vue-transitions'
import '@morev/vue-transitions/styles'
import globalEventBus from './globalEventBus'

/** COMPONENTS */
import SideNav from './components/SideNav.vue'
import TopNav from './components/TopNav.vue'
import DropdownMenu from './components/DropdownMenu.vue'
import ContactModal from './components/ContactModal.vue'
import CarrierSelectionModal from './components/CarrierSelectionModal.vue'
import CallsTable from './components/CallsTable.vue'
import CIPTable from './components/CIPTable.vue'
import CarrierListModal from './components/CarrierListModal.vue'
import NumbersAssignedModal from './components/NumbersAssignedModal.vue'
import AmendBalanceModal from './components/AmendBalanceModal.vue'
import BalanceHistoryModal from './components/BalanceHistoryModal.vue'
import BarredCliModal from './components/BarredCliModal.vue'
import BarredCliEditModal from './components/BarredCliEditModal.vue'
import TariffEditModal from './components/TariffEditModal.vue'
import LCRsDestinationsModal from './components/LCRsDestinationsModal.vue'
import RegisteredSIMsTable from './components/RegisteredSIMsTable.vue'
import TTSDialog from './components/TTSDialog.vue'
import UserSignupModal from './components/UserSignupModal.vue'
import RegistrationsTable from './components/RegistrationsTable.vue'
import MediaPlayer from './components/MediaPlayer.vue'
import UploadFileAudio from './components/UploadFileAudio.vue'
import UploadRecordAudio from './components/UploadRecordAudio.vue'
import QueuedCallsTable from './components/QueuedCallsTable.vue'
import PageHeader from '@/components/common/PageHeader.vue'
import FilterBar from '@/components/common/FilterBar.vue'
import FilterSection from '@/components/common/FilterSection.vue'
import UploadFileBulkModal from '@/components/UploadFileBulkModal.vue'
import ActiveFilters from '@/components/common/ActiveFilters.vue'
import Pagination from '@/components/common/Pagination.vue'
import ApplicationConfig from '@/components/common/ApplicationConfig.vue'
import DataView from '@/components/common/DataView.vue'
import GenericTable from '@/components/common/GenericTable.vue'
import TableHeader from '@/components/common/TableHeader.vue'
import ViewToggle from '@/components/common/ViewToggle.vue'
import SearchField from '@/components/common/SearchField.vue'
import StatusSelect from '@/components/common/StatusSelect.vue'
import TenantSelect from '@/components/common/TenantSelect.vue'
import AccountSelect from '@/components/common/AccountSelect.vue'
import DateRangePicker from '@/components/common/DateRangePicker.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import FormField from '@/components/common/FormField.vue'
import FormSection from '@/components/common/FormSection.vue'
import FilterPill from '@/components/common/FilterPill.vue'
import FilterPills from '@/components/FilterPills.vue'
import FileUploadButton from '@/components/common/FileUploadButton.vue'
import BackButton from '@/components/common/BackButton.vue'
import ActionButtons from '@/components/common/ActionButtons.vue'
import CardWrapper from '@/components/common/CardWrapper.vue'
import QRCodeModal from './components/QRCodeModal.vue'
import SearchableDropdown from '@/components/common/SearchableDropdown.vue'

require('./styles/main.scss')

if (process.env.FORCE_API_URL) {
	window.dtServerUrl = process.env.FORCE_API_URL
} else {
	if (window.location.hostname !== 'localhost') {
		window.dtServerUrl = `https://api.${window.location.hostname}`
	} else {
		window.dtServerUrl = window.location.protocol + '//' + window.location.hostname + ':3005'
	}
}

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

axios.interceptors.request.use(
	async (config) => {
		try {
			const access_token = await auth0.getAccessTokenSilently()
			if (access_token) {
				config.headers['Authorization'] = 'Bearer ' + access_token
			}
		} catch (err) {
			console.error('Getting Access Token failed', err)
		}

		return config
	},
	(error) => Promise.reject(error)
)

axios.interceptors.response.use(
	(response) => response,
	(error) => {
		if (error?.code === 'ERR_NETWORK') {
			if (!isMobile) {
				globalEventBus.emit('network-error', error)
			} else {
				console.warn('Network error suppressed on mobile device:', error)
			}
		} else {
			throw error
		}
	}
)

/** CHART COMPONENTS */
import PieChart from './components/charts/PieChart.vue'
import LineChart from './components/charts/LineChart.vue'
import BarChart from './components/charts/BarChart.vue'

import { JsPlumbToolkitVue3Plugin } from '@jsplumbtoolkit/browser-ui-vue3'

import VueSweetalert2 from 'vue-sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'

import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

import { plugin, defaultConfig } from '@formkit/vue'
import './registerServiceWorker'
import './assets/tailwind.css'

const app = createApp(App)

app.config.errorHandler = (err, vm, info) => {
	console.error('Vue Error:', err, vm, info)
	return false
}

app.component('PieChart', PieChart)
app.component('LineChart', LineChart)
app.component('BarChart', BarChart)
app.component('TopNav', TopNav)
app.component('SideNav', SideNav)
app.component('DropdownMenu', DropdownMenu)
app.component('ContactModal', ContactModal)
app.component('AmendBalanceModal', AmendBalanceModal)
app.component('BalanceHistoryModal', BalanceHistoryModal)
app.component('CarrierSelectionModal', CarrierSelectionModal)
app.component('CarrierListModal', CarrierListModal)
app.component('LcrsDestinationsModal', LCRsDestinationsModal)
app.component('NumbersAssignedModal', NumbersAssignedModal)
app.component('BarredCliModal', BarredCliModal)
app.component('BarredCliEditModal', BarredCliEditModal)
app.component('CallsTable', CallsTable)
app.component('CipTable', CIPTable)
app.component('TariffEditModal', TariffEditModal)
app.component('UserSignupModal', UserSignupModal)
app.component('TtsDialog', TTSDialog)
app.component('RegistrationsTable', RegistrationsTable)
app.component('RegisteredSimsTable', RegisteredSIMsTable)
app.component('QueuedCallsTable', QueuedCallsTable)
app.component('MediaPlayer', MediaPlayer)
app.component('UploadRecordAudio', UploadRecordAudio)
app.component('UploadFileAudio', UploadFileAudio)
app.component('VueDatePicker', VueDatePicker)
app.component('ApplicationConfig', ApplicationConfig)
// COMMON COMPONENTS
app.component('PageHeader', PageHeader)
app.component('FilterBar', FilterBar)
app.component('FilterSection', FilterSection)
app.component('ActiveFilters', ActiveFilters)
app.component('Pagination', Pagination)
app.component('DataView', DataView)
app.component('GenericTable', GenericTable)
app.component('TableHeader', TableHeader)
app.component('ViewToggle', ViewToggle)
app.component('SearchField', SearchField)
app.component('StatusSelect', StatusSelect)
app.component('TenantSelect', TenantSelect)
app.component('AccountSelect', AccountSelect)
app.component('DateRangePicker', DateRangePicker)
app.component('LoadingSpinner', LoadingSpinner)
app.component('FormField', FormField)
app.component('UploadFileBulkModal', UploadFileBulkModal)
app.component('FormSection', FormSection)
app.component('FilterPill', FilterPill)
app.component('FilterPills', FilterPills)
app.component('FileUploadButton', FileUploadButton)
app.component('BackButton', BackButton)
app.component('ActionButtons', ActionButtons)
app.component('CardWrapper', CardWrapper)
app.component('QRCodeModal', QRCodeModal)
app.component('SearchableDropdown', SearchableDropdown)

app.use(router)
app.use(VueSweetalert2)
app.use(auth0)
app.use(VueTransitions)
app.use(JsPlumbToolkitVue3Plugin, {})
app.use(plugin, defaultConfig)

app.config.globalProperties.$global = {
	prettyPrint(text) {
		return text
			? text
					.split('_')
					.map((word) =>
						word === 'sip' || word === 'bt' || word === 'id' || word === 'api' || word === 'cli'
							? word.toUpperCase()
							: word[0].toUpperCase() + word.substring(1)
					)
					.join(' ')
			: ''
	},
	isMobile,
	formatObjectProperty(value) {
		if (value === null || value === undefined) {
			return 'Not set'
		}

		if (typeof value === 'object' && Object.keys(value).length === 0) {
			return 'No settings'
		}

		if (Array.isArray(value)) {
			return value.length === 0 ? 'No options' : value.length + ' settings'
		}

		if (typeof value === 'object') {
			if (Array.isArray(value)) {
				if (value.length === 0) return 'Empty array'

				if (typeof value[0] === 'object') {
					if (value[0].name) {
						return value.map((item) => item.name).join(', ')
					} else if (value[0].title) {
						return value.map((item) => item.title).join(', ')
					} else if (value[0].id) {
						return value.map((item) => `ID: ${item.id}`).join(', ')
					} else if (value[0].key && value[0].value) {
						return value.map((item) => `${item.key}: ${item.value}`).join(', ')
					}
				}

				return value.join(', ')
			}

			if (value.rules || value.exceptions) {
				return 'Rules Apply'
			}

			if (value.displayName) return value.displayName
			if (value.name) return value.name
			if (value.title) return value.title
			if (value.id) return `ID: ${value.id}`

			if (Object.keys(value).length <= 3) {
				return Object.entries(value)
					.map(([k, v]) => `${k}: ${typeof v === 'object' ? 'Object' : v}`)
					.join(', ')
			}

			return `${Object.keys(value).length} settings`
		}

		if (typeof value === 'boolean') return value ? 'Yes' : 'No'
		if (typeof value === 'number') return value.toString()

		return value
	},
	formatDuration(seconds) {
		if (seconds < 60) {
			return `${seconds}s`
		}
		const minutes = Math.floor(seconds / 60)
		const remainingSeconds = seconds % 60
		if (minutes < 60) {
			return `${minutes}m ${remainingSeconds}s`
		}
		const hours = Math.floor(minutes / 60)
		const remainingMinutes = minutes % 60
		return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`
	},
	normalCase(str) {
		if (str) return str.replace(/_/g, ' ').toProperCase()
		else return ''
	},
	dateFormat(date) {
		if (date) return moment(date).format('DD/MM/YY')
		else return ''
	},
	dateTimeFormat(date) {
		if (date) return moment(date).format('DD/MM/YY HH:mm:ss')
		else return ''
	},
	dateWordFormat(date) {
		if (date) return moment(date).format('dddd, MMMM Do YYYY, HH:mm:ss')
		else return ''
	},
	currencyGbp(amount, precision) {
		if (amount) return `£${parseFloat(amount).toFixed(precision || 4)}`
		else return '£0.00'
	},
	numberWithCommas(num) {
		if (!num) return '0'
		return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
	},
	hasUserRole(roles, role) {
		roles = roles || []
		return roles.includes(role)
	},
	authType(type) {
		switch (type) {
			case 'SOURCE_IP':
				return 'Source IP(s)'
			case 'SIP_HEADER':
				return 'Custom SIP Header'
			default:
				return '(not set)'
		}
	},
	setSelectedTenant(event) {
		try {
			window.localStorage.setItem('selectedTenant', event.target.value)
		} catch (e) {
			console.log(`Error setting selected tenant in localStorage`, e)
		}
	},
	getSelectedTenant() {
		try {
			return parseInt(window.localStorage.getItem('selectedTenant'))
		} catch (e) {
			console.log(`Error getting selected tenant from localStorage`, e)
		}
	},
	clearSelectedTenant() {
		try {
			window.localStorage.removeItem('selectedTenant')
		} catch (e) {
			console.log(`Error clearing selected tenant from localStorage`, e)
		}
	},
	clearSelectedAccount() {
		try {
			window.localStorage.removeItem('selectedAccount')
		} catch (e) {
			console.log(`Error clearing selected account from localStorage`, e)
		}
	},
	setSelectedAccount(event) {
		try {
			console.log('set Account', event)
			window.localStorage.setItem('selectedAccount', event.target.value)
		} catch (e) {
			console.log(`Error setting selected account in localStorage`, e)
		}
	},
	getSelectedAccount() {
		try {
			return parseInt(window.localStorage.getItem('selectedAccount'))
		} catch (e) {
			console.log(`Error getting selected account from localStorage`, e)
		}
	}
}
;(app.config.globalProperties.createTooltip = function createTooltip(event, text, side = 'top', offset = [0, 10]) {
	let target = event.target

	// Check if the text is overflowing
	if (target.offsetWidth < target.scrollWidth) {
		// Create tooltip element
		let tooltip = document.createElement('div')

		if (tooltip) {
			tooltip.className = 'popper max-w-2xl'
			tooltip.innerHTML = text
			tooltip.style.backgroundColor = '#444A58'
			tooltip.style.color = 'white'
			tooltip.style.padding = '10px'
			tooltip.style.borderRadius = '8px'
			document.body.appendChild(tooltip)

			// Create popper instance
			let popperInstance = createPopper(target, tooltip, {
				placement: side,
				modifiers: [
					{
						name: 'offset',
						options: {
							offset: offset // moves the tooltip based on the provided offset
						}
					}
				]
			})

			// Show the tooltip
			tooltip.setAttribute('data-show', '')

			// Handle click to copy text
			target.addEventListener('click', () => {
				navigator.clipboard.writeText(text)
				// Destroy the tooltip after copying
				popperInstance.destroy()
				if (tooltip.parentNode) {
					tooltip.parentNode.removeChild(tooltip)
				}
			})

			// Handle mouse leave to destroy tooltip
			target.addEventListener('mouseleave', () => {
				popperInstance.destroy()
				if (tooltip.parentNode) {
					tooltip.parentNode.removeChild(tooltip)
				}
			})
		}
	}
}),
	(app.config.globalProperties.tooltip = function tooltip(event, text, placement = 'top', offset = [0, 10]) {
		let target = event.target
		let tooltip = document.createElement('div')
		if (tooltip) {
			tooltip.className = 'popper max-w-2xl'
			tooltip.innerHTML = text
			tooltip.style.backgroundColor = '#444A58'
			tooltip.style.color = 'white'
			tooltip.style.padding = '10px'
			tooltip.style.borderRadius = '8px'
			tooltip.style.zIndex = '50'
			document.body.appendChild(tooltip)
			let popperInstance = createPopper(target, tooltip, {
				placement: placement,
				modifiers: [
					{
						name: 'offset',
						options: {
							offset: offset
						}
					}
				]
			})
			tooltip.setAttribute('data-show', '')
			target.addEventListener('click', () => {
				navigator.clipboard.writeText(text)
				popperInstance.destroy()
				if (tooltip.parentNode) {
					tooltip.parentNode.removeChild(tooltip)
				}
			})
			target.addEventListener('mouseleave', () => {
				popperInstance.destroy()
				if (tooltip.parentNode) {
					tooltip.parentNode.removeChild(tooltip)
				}
			})
		}
	}),
	app.mount('#app')

String.prototype.toProperCase = function () {
	return this.replace(/\w\S*/g, function (txt) {
		return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
	})
}

export { app }
